import { Component, OnInit } from '@angular/core'
import { BehaviorSubject, Observable } from 'rxjs'
import { CrudOps } from '../../../api/constants/crud.enum'
import { Client } from '../../../api/models/client.model'
import { TimesheetProject } from '../../../api/models/timesheet-project.model'
import { ClientService } from '../../../api/services/client.service'
import { DialogService } from '../../common/services/dialog.service'
import { LoaderService } from '../../common/services/loader.service'
import { PpwLocalStorageService } from '../../common/services/ppwlocalstorage.service'
import { SnackbarService } from '../../common/services/snackbar.service'
import { UnsubscribeOnDestroyAdapter } from '../../shared/adapter/unsubscribe-on-destroy-adapter'
import { ClientsDialogComponent } from './clients-dialog/clients-dialog.component'
import { ProjectDialogComponent } from './project-dialog/project-dialog.component'

interface Container {
    type: string
    isExpanded: boolean
    item: Client
}

export interface ClientContainer extends Client, Container {}

export interface TimesheetProjectContainer extends TimesheetProject, Container {}

@Component({
    selector: 'ppw-clients-and-projects',
    templateUrl: './clients-and-projects.component.html',
    styleUrls: ['./clients-and-projects.component.scss']
})
export class ClientsAndProjectsComponent extends UnsubscribeOnDestroyAdapter implements OnInit {
    pageTitle = 'Clients and Projects'
    selectedClient?: ClientContainer
    selectedTimesheetProject?: TimesheetProject
    displayedColumns: Array<string> = ['name', 'icons', 'issueKey', 'invoiceId', 'versionId', 'projectKey']
    datasource$: Observable<Array<ClientContainer | TimesheetProjectContainer>>
    allClients!: Array<ClientContainer>

    private readonly prefix = 'ClientsAndProjectsComponent'
    private readonly datasourceSubject: BehaviorSubject<Array<ClientContainer | TimesheetProjectContainer>> =
        new BehaviorSubject(undefined) as unknown as BehaviorSubject<Array<ClientContainer | TimesheetProjectContainer>>
    private latestDatasource?: Array<ClientContainer | TimesheetProjectContainer>

    constructor(
        private readonly loaderService: LoaderService,
        private readonly clientService: ClientService,
        private readonly ppwLocalStorageService: PpwLocalStorageService,
        private readonly snackbarService: SnackbarService,
        private readonly dialogService: DialogService
    ) {
        super()
        this.datasource$ = this.datasourceSubject.asObservable()
    }

    async ngOnInit(): Promise<void> {
        await this.fetchAllClients()
        this.subscriptions.add(
            this.datasource$.subscribe((datasource) => {
                this.allClients = datasource.filter(
                    (clientOrTimesheet) => clientOrTimesheet.type === 'client'
                ) as Array<ClientContainer>
            })
        )
    }

    select(client: ClientContainer): void {
        client.isExpanded = !client.isExpanded
        if (!client.isExpanded) {
            client.timesheetProjects.forEach(() => {
                if (this.latestDatasource !== undefined) {
                    const timesheetProjectToDeleteIndex = this.latestDatasource.findIndex(
                        (datasource) =>
                            datasource.type === 'timesheetProject' &&
                            (datasource as TimesheetProjectContainer).clientId === client.id
                    )
                    this.latestDatasource.splice(timesheetProjectToDeleteIndex, 1)
                }
            })
            this.selectedTimesheetProject = undefined
            this.ppwLocalStorageService.removeStoredData(
                this.ppwLocalStorageService.STORAGE_KEY_SELECTED_PROJECT_ID.key
            )
        } else {
            if (this.latestDatasource !== undefined) {
                const index = this.latestDatasource.findIndex((e) => e.id === client.id)
                const timesheetprojects: Array<TimesheetProjectContainer> =
                    client.timesheetProjects as Array<TimesheetProjectContainer>
                const sortedTimesheets = timesheetprojects.sort((a, b) => a.name.localeCompare(b.name))
                sortedTimesheets.forEach((t) => ((t as TimesheetProjectContainer).type = 'timesheetProject'))
                this.latestDatasource.splice(index, 1, client, ...sortedTimesheets)
            }
        }
        if (this.latestDatasource !== undefined) {
            this.datasourceSubject.next(this.latestDatasource)
            this.selectClient(client)
        }
    }

    selectTimesheetProject(timesheetProject: TimesheetProject): void {
        if (
            this.selectedClient === undefined ||
            !(
                'timesheetProjects' in this.selectedClient &&
                this.selectedClient.timesheetProjects.includes(timesheetProject)
            )
        ) {
            if (this.latestDatasource) {
                this.selectClient(
                    this.latestDatasource.find(
                        (c) => c.type === 'client' && c.id === timesheetProject.clientId
                    ) as ClientContainer
                )
            }
        }
        if (this.selectedTimesheetProject !== timesheetProject) {
            this.selectedTimesheetProject = timesheetProject
            this.ppwLocalStorageService.storeData(
                this.ppwLocalStorageService.STORAGE_KEY_SELECTED_PROJECT_ID,
                this.selectedTimesheetProject.id
            )
        } else {
            this.selectedTimesheetProject = undefined
            this.ppwLocalStorageService.removeStoredData(
                this.ppwLocalStorageService.STORAGE_KEY_SELECTED_PROJECT_ID.key
            )
        }
    }

    isTimesheetProject(_: number | undefined, rowData: Container): boolean {
        return rowData.type === 'timesheetProject'
    }

    isClient(_: number | undefined, rowData: Container): boolean {
        return rowData.type === 'client'
    }

    archive(): void {
        if (this.selectedClient && !this.selectedTimesheetProject) {
            this.showClientDialog(CrudOps.Delete, this.selectedClient)
        } else if (this.selectedTimesheetProject) {
            this.showProjectDialog(CrudOps.Delete, this.selectedTimesheetProject, this.allClients)
        }
    }

    edit(): void {
        if (this.selectedClient && !this.selectedTimesheetProject) {
            this.editClient(this.selectedClient)
        } else if (this.selectedTimesheetProject) {
            this.editTimesheetProject(this.selectedTimesheetProject)
        }
    }

    editClient(client: Client): void {
        this.showClientDialog(CrudOps.Update, client)
    }

    editTimesheetProject(timesheetProject: TimesheetProject): void {
        this.showProjectDialog(CrudOps.Update, timesheetProject, this.allClients)
    }

    newProject(): void {
        const project = new TimesheetProject()
        this.showProjectDialog(CrudOps.Create, project, this.allClients)
    }

    createClient(): void {
        const client = new Client()
        this.showClientDialog(CrudOps.Create, client)
    }

    private selectClient(client: ClientContainer): void {
        if (this.selectedClient !== client) {
            this.selectedClient = client
            this.ppwLocalStorageService.storeData(
                this.ppwLocalStorageService.STORAGE_KEY_SELECTED_CLIENT_ID,
                this.selectedClient.id
            )
        } else {
            this.selectedClient = undefined
            this.ppwLocalStorageService.removeStoredData(this.ppwLocalStorageService.STORAGE_KEY_SELECTED_CLIENT_ID.key)
        }
    }

    private async fetchAllClients(): Promise<void> {
        this.allClients = new Array<ClientContainer>()
        this.loaderService.startLoading()
        try {
            const paginatedListClients = await this.clientService.getAllClients()
            paginatedListClients.items.forEach((i) => ((i as ClientContainer).type = 'client'))
            this.latestDatasource = paginatedListClients.items as Array<ClientContainer>
            this.datasourceSubject.next(this.latestDatasource)
            const storedClientId = this.ppwLocalStorageService.getStoredData(
                this.ppwLocalStorageService.STORAGE_KEY_SELECTED_CLIENT_ID
            )
            this.selectedClient = undefined
            this.selectedTimesheetProject = undefined
            const foundClient: ClientContainer = this.latestDatasource.find(
                (datasource: ClientContainer | TimesheetProjectContainer) =>
                    datasource.type === 'client' && datasource.id === storedClientId
            ) as ClientContainer
            if (foundClient) {
                this.select(foundClient)
                const storedProjectId = this.ppwLocalStorageService.getStoredData(
                    this.ppwLocalStorageService.STORAGE_KEY_SELECTED_PROJECT_ID
                )
                const foundProject: TimesheetProject | undefined = foundClient.timesheetProjects.find(
                    (t) => t.id === storedProjectId
                )
                if (foundProject) {
                    this.selectTimesheetProject(foundProject as TimesheetProjectContainer)
                }
            }
        } catch (err) {
            this.snackbarService.handleError(this.prefix, err)
        } finally {
            this.loaderService.stopLoading()
        }
    }

    private showClientDialog(crud: CrudOps, client: Client): void {
        this.dialogService
            .open(ClientsDialogComponent, { client, crud })
            .afterClosed()
            .subscribe(async (response) => {
                if (response !== undefined) {
                    if (response.crud === CrudOps.Create) {
                        this.snackbarService.promtSnackBar(`Client ${response.client.name.toUpperCase()} created`)
                    } else if (response.crud === CrudOps.Update) {
                        this.snackbarService.promtSnackBar(`Client ${response.client.name.toUpperCase()} updated`)
                    } else if (response.crud === CrudOps.Delete) {
                        this.snackbarService.promtSnackBar(`Client ${response.client.name.toUpperCase()} archived`)
                    }
                    this.selectedClient = response.crud !== CrudOps.Delete ? response.client : undefined
                    if (this.selectedClient !== undefined) {
                        this.ppwLocalStorageService.storeData(
                            this.ppwLocalStorageService.STORAGE_KEY_SELECTED_CLIENT_ID,
                            this.selectedClient.id
                        )
                    }
                    await this.fetchAllClients()
                }
            })
    }

    private showProjectDialog(crud: CrudOps, project: TimesheetProject, clients: Array<ClientContainer>): void {
        this.dialogService
            .open(ProjectDialogComponent, {
                project,
                crud,
                clients
            })
            .afterClosed()
            .subscribe(async (response) => {
                if (response !== undefined) {
                    if (response.crud === CrudOps.Create) {
                        this.snackbarService.promtSnackBar(
                            `Project ${response.project.name.toUpperCase()} created for client ${response.project.clientName.toUpperCase()}`
                        )
                    } else if (response.crud === CrudOps.Update) {
                        this.snackbarService.promtSnackBar(
                            `Project ${response.project.name.toUpperCase()} updated for client ${response.project.clientName.toUpperCase()}`
                        )
                    } else if (response.crud === CrudOps.Delete) {
                        this.snackbarService.promtSnackBar(
                            `${response.project.name.toUpperCase()} for client ${response.project.clientName.toUpperCase()}`
                        )
                    }
                    if (this.latestDatasource) {
                        this.selectedClient = this.latestDatasource.find(
                            (client) => client.id === response.project.clientId
                        ) as ClientContainer
                    }
                    if (this.selectedClient) {
                        this.ppwLocalStorageService.storeData(
                            this.ppwLocalStorageService.STORAGE_KEY_SELECTED_CLIENT_ID,
                            this.selectedClient.id
                        )
                    }
                    this.ppwLocalStorageService.storeData(
                        this.ppwLocalStorageService.STORAGE_KEY_SELECTED_PROJECT_ID,
                        response.project.id
                    )
                    await this.fetchAllClients()
                }
            })
    }
}
